/**
 * Created by neo on 10.01.2026
 * Challenge Location Screen - Dedicated screen for a specific location
 */
import * as React from 'react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import dayjs from 'dayjs';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { RouteChallenge, RouteChallengeApi } from '../../Model/Engagement/RouteChallenge/RouteChallenge';
import { RouteChallengeLocation } from '../../Model/Engagement/RouteChallenge/RouteChallengeLocation';
import { RouteChallengeTeam, RouteChallengeTeamApi } from '../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { IndividualChallenge, IndividualChallengeApi } from '../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { IndividualChallengeParticipant, IndividualChallengeParticipantApi } from '../../Model/Engagement/IndividualChallenge/IndividualChallengeParticipant';
import { CommunityWallPost, REACTION_TYPES, CommunityWallPostApi } from '../../Model/Engagement/CommunityWall/CommunityWallPost';
import { usePostReactions } from '../../Hooks/usePostReactions';
import { LoadingContainer } from '../../Components/LoadingContainer';
import { MarkdownRenderer } from '../../Components/Markdown/MarkdownRenderer';
import { Button } from '../../Components/Button';
import { CreatePostModal } from './Components/CreatePostModal';
import { Theme } from '../../Theme/Theme';
import { getMediaLarge, getMediaMedium, getMediaSmallest } from '../../Model/Media/Media';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeInOverlay = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

type MediaViewerItem = {
  url: string;
  type: 'image' | 'video';
  poster?: string;
};

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  animation: ${fadeInOverlay} 0.2s ease-out;
`;

const FullscreenHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: white;
`;

const FullscreenTitle = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
`;

const FullscreenClose = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const FullscreenContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${zoomIn} 0.3s ease-out;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
  
  video {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
  }
`;

const FullscreenNav = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
`;

const FullscreenNavButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const FullscreenCounter = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  min-width: 60px;
  text-align: center;
`;

const FullscreenThumbnails = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 0 24px 24px;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const FullscreenThumbnail = styled.button<{ isActive?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid ${props => props.isActive ? 'white' : 'transparent'};
  padding: 0;
  cursor: pointer;
  opacity: ${props => props.isActive ? 1 : 0.5};
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PageContainer = styled(Container)`
  padding-top: 0;
  padding-bottom: 48px;
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
`;

const HeroSection = styled.div<{ hasMedia?: boolean; isReached?: boolean }>`
  position: relative;
  width: 100%;
  min-height: ${props => props.hasMedia ? '400px' : '280px'};
  background: ${props => props.isReached 
    ? 'linear-gradient(135deg, #00dda2 0%, #00AF96 50%, #008B78 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  
  @media (min-width: 768px) {
    min-height: ${props => props.hasMedia ? '500px' : '320px'};
  }
`;

const HeroMedia = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, transparent 100%);
    pointer-events: none;
  }
`;

const HeroExpandButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 5;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: none;
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: scale(1.1);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 32px 24px;
  color: white;
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (min-width: 768px) {
    padding: 48px;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }
`;

const BackButton = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 16px;
  border-radius: 24px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-4px);
  }
`;

const LocationHeader = styled.div`
  margin-bottom: 0;
`;

const LocationBadge = styled.span<{ variant: 'reached' | 'locked' | 'start' | 'end' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 16px;
  background: ${(props) => {
    switch (props.variant) {
      case 'reached':
        return 'rgba(255, 255, 255, 0.25)';
      case 'start':
        return 'rgba(34, 197, 94, 0.9)';
      case 'end':
        return 'rgba(239, 68, 68, 0.9)';
      case 'locked':
        return 'rgba(255, 255, 255, 0.15)';
      default:
        return 'rgba(255, 255, 255, 0.15)';
    }
  }};
  color: white;
  backdrop-filter: blur(10px);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LocationTitle = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: white;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const LocationMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 500;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const ContentRow = styled(Row)`
  margin-top: 24px;
`;

const MainContent = styled(Col)``;

const SidebarContent = styled(Col)`
  @media (min-width: 992px) {
    position: sticky;
    top: 80px;
    align-self: flex-start;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DescriptionContent = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: ${Theme.Colors.charcoal};

  p {
    margin-bottom: 12px;
  }

  h1,
  h2,
  h3 {
    color: ${Theme.Colors.textColor};
    margin-top: 20px;
    margin-bottom: 12px;
  }
`;

const MediaGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const MediaItem = styled.div<{ isVideo?: boolean; isLarge?: boolean }>`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: ${props => props.isLarge ? '16/10' : '4/3'};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  
  ${props => props.isLarge && `
    grid-column: span 2;
    
    @media (max-width: 768px) {
      grid-column: span 1;
    }
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img,
  &:hover video {
    transform: scale(1.05);
  }
`;

const VideoPlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: ${Theme.Colors.primary};
  }
`;

const MediaOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%);
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const LockedOverlay = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 24px;
  padding: 60px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.5) 50%,
      transparent 70%
    );
    animation: ${shimmer} 3s infinite;
  }
`;

const LockedIcon = styled.span`
  font-size: 80px;
  display: block;
  margin-bottom: 20px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LockedTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: ${Theme.Colors.textColor};
  position: relative;
`;

const LockedDescription = styled.p`
  font-size: 15px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  position: relative;
`;

const PointsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, ${Theme.Colors.primary} 0%, #00AF96 100%);
  color: white;
  padding: 14px 28px;
  border-radius: 30px;
  font-size: 18px;
  font-weight: 700;
  box-shadow: 0 4px 15px rgba(0, 221, 163, 0.3);
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 221, 163, 0.4);
  }
`;

const ProgressInfo = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${Theme.Colors.lightestGrey};
  position: relative;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: linear-gradient(90deg, ${Theme.Colors.primary} 0%, #00AF96 100%);
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ProgressText = styled.div`
  font-size: 13px;
  color: ${Theme.Colors.warmGrey};
  text-align: center;
`;

const PostCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid ${Theme.Colors.lightestGrey};
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const PostAvatar = styled.div<{ imageUrl?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.primary)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const PostAuthor = styled.div`
  flex: 1;
`;

const PostAuthorName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const PostDate = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
`;

const PostText = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
`;

const PostMediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
`;

const PostMediaItem = styled.div<{ isVideo?: boolean }>`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  cursor: pointer;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img, &:hover video {
    transform: scale(1.05);
  }
`;

const PostVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  
  span {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }
`;

const PostReactions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${Theme.Colors.lightestGrey};
`;

const PostReactionButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const PostReactionButton = styled.button<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border-radius: 12px;
  border: none;
  background: ${props => props.isActive ? 'rgba(0, 221, 163, 0.15)' : Theme.Colors.lightestGrey};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.isActive ? 'rgba(0, 221, 163, 0.25)' : Theme.Colors.lighterGrey};
    transform: scale(1.05);
  }
`;

const PostReactionCount = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
`;

const EmptyPosts = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: ${Theme.Colors.warmGrey};
  font-size: 14px;
  background: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  
  span {
    font-size: 40px;
    display: block;
    margin-bottom: 12px;
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 24px;
`;

const ErrorEmoji = styled.span`
  font-size: 64px;
  display: block;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ErrorDescription = styled.p`
  color: ${Theme.Colors.charcoal};
`;

type ChallengeType = 'route' | 'individual';

export const ChallengeLocationScreen: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { challengeId, locationId } = useParams<{ challengeId: string; locationId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<RouteChallenge | IndividualChallenge | null>(null);
  const [challengeType, setChallengeType] = useState<ChallengeType | null>(null);
  const [location, setLocation] = useState<RouteChallengeLocation | null>(null);
  const [locationIndex, setLocationIndex] = useState(0);
  const [myTeam, setMyTeam] = useState<RouteChallengeTeam | null>(null);
  const [myParticipation, setMyParticipation] = useState<IndividualChallengeParticipant | null>(null);
  const [posts, setPosts] = useState<CommunityWallPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState<MediaViewerItem[] | null>(null);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false);

  // Hooks MUST be called before any early returns (Rules of Hooks)
  const locationName = useLocalized(location?.name) || `Location ${locationIndex + 1}`;
  const locationDescription = useLocalized(location?.description);
  const challengeName = useLocalized(challenge?.name);

  useEffect(() => {
    if (!challengeId || !locationId) {
      setError('Missing challenge or location ID');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try Individual Challenge first
        const individual = await IndividualChallengeApi.findOne(challengeId);
        if (individual) {
          setChallenge(individual);
          setChallengeType('individual');

          const loc = individual.locations.find((l) => l.id === locationId);
          if (loc) {
            setLocation(loc);
            setLocationIndex(individual.locations.indexOf(loc));
          }

          const participation = await IndividualChallengeParticipantApi.myParticipation(challengeId);
          setMyParticipation(participation ?? null);
          setLoading(false);
          return;
        }

        // Try Route Challenge
        const route = await RouteChallengeApi.findOne(challengeId);
        if (route) {
          setChallenge(route);
          setChallengeType('route');

          const loc = route.locations.find((l) => l.id === locationId);
          if (loc) {
            setLocation(loc);
            setLocationIndex(route.locations.indexOf(loc));
          }

          const team = await RouteChallengeTeamApi.myTeam(challengeId).catch(() => null);
          setMyTeam(team);
          setLoading(false);
          return;
        }

        setError('Challenge not found');
      } catch (err) {
        console.error('Error fetching location:', err);
        setError('Failed to load location');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [challengeId, locationId]);

  useEffect(() => {
    if (!challengeId || !locationId) return;

    const fetchPosts = async () => {
      setLoadingPosts(true);
      try {
        const locationPosts = await CommunityWallPostApi.findByLocation(challengeId, locationId, { size: 20 });
        setPosts(locationPosts);
      } catch (err) {
        console.error('Error fetching posts:', err);
      } finally {
        setLoadingPosts(false);
      }
    };

    fetchPosts();
  }, [challengeId, locationId]);

  const handleBack = useCallback(() => {
    navigate(`/motivation/challenge/${challengeId}`);
  }, [navigate, challengeId]);

  const handlePostCreated = useCallback(async (post: CommunityWallPost) => {
    // Add the new post to the beginning of the list
    setPosts((prev) => [post, ...prev]);

    // Optionally, refetch all posts to ensure consistency
    if (challengeId && locationId) {
      try {
        const updatedPosts = await CommunityWallPostApi.findByLocation(challengeId, locationId, { size: 20 });
        setPosts(updatedPosts);
      } catch (err) {
        console.error('Error refreshing posts:', err);
      }
    }
  }, [challengeId, locationId]);

  const openFullscreen = useCallback((mediaItems: MediaViewerItem[], startIndex: number = 0) => {
    setFullscreenMedia(mediaItems);
    setFullscreenIndex(startIndex);
  }, []);

  const closeFullscreen = useCallback(() => {
    setFullscreenMedia(null);
    setFullscreenIndex(0);
  }, []);

  const goToPrevMedia = useCallback(() => {
    setFullscreenIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const goToNextMedia = useCallback(() => {
    if (fullscreenMedia) {
      setFullscreenIndex((prev) => Math.min(fullscreenMedia.length - 1, prev + 1));
    }
  }, [fullscreenMedia]);

  const locationMediaItems = useMemo((): MediaViewerItem[] => {
    if (!location?.medias) return [];
    return location.medias.map((media) => ({
      url: getMediaLarge(media),
      type: media.mediaType?.startsWith('video/') ? 'video' : 'image',
      poster: getMediaMedium(media),
    }));
  }, [location]);

  const openVideoFullscreen = useCallback((videoElement: HTMLVideoElement | null) => {
    if (!videoElement) return;
    if (videoElement.requestFullscreen) {
      videoElement.requestFullscreen();
    } else if ((videoElement as any).webkitRequestFullscreen) {
      (videoElement as any).webkitRequestFullscreen();
    } else if ((videoElement as any).msRequestFullscreen) {
      (videoElement as any).msRequestFullscreen();
    }
    videoElement.play();
  }, []);

  const handleMediaClick = useCallback((media: any, index: number) => {
    const isVideo = media.mediaType?.startsWith('video/');
    if (isVideo) {
      // For videos, find the video element and make it fullscreen natively
      const videoEl = document.querySelector(`[data-media-id="${media.id || index}"]`) as HTMLVideoElement;
      openVideoFullscreen(videoEl);
    } else {
      // For images, open the custom fullscreen viewer
      openFullscreen(locationMediaItems, index);
    }
  }, [openVideoFullscreen, openFullscreen, locationMediaItems]);

  const reachedLocations = useMemo(() => {
    if (challengeType === 'individual') {
      return myParticipation?.reachedLocations || [];
    }
    return myTeam?.reachedLocations || [];
  }, [challengeType, myParticipation, myTeam]);

  const isReached = useMemo(() => {
    if (!location) return false;
    // First location is always reached after challenge starts
    if (locationIndex === 0 && challenge?.started) return true;
    return reachedLocations.some((r) => r.locationId === location.id);
  }, [location, locationIndex, challenge, reachedLocations]);

  const isFirst = locationIndex === 0;
  const isLast = challenge ? locationIndex === challenge.locations.length - 1 : false;

  if (loading) {
    return <LoadingContainer text={t('location.loading', { defaultValue: 'Loading location...' })} />;
  }

  if (error || !challenge || !location) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorEmoji>🔍</ErrorEmoji>
          <ErrorTitle>{t('location.notFound', { defaultValue: 'Location Not Found' })}</ErrorTitle>
          <ErrorDescription>
            {t('location.notFoundDescription', { defaultValue: 'Could not load this location.' })}
          </ErrorDescription>
        </ErrorContainer>
      </PageContainer>
    );
  }

  const heroMedia = location.medias?.[0] || location.image;
  const hasHeroMedia = !!heroMedia;

  return (
    <PageContainer>
      <HeroSection hasMedia={hasHeroMedia} isReached={isReached}>
        <BackButton onClick={handleBack}>
          ← {t('location.backToChallenge', { defaultValue: 'Back to Challenge' })}
        </BackButton>
        
        {hasHeroMedia && (
          <>
            <HeroMedia onClick={() => handleMediaClick(heroMedia, 0)}>
              {heroMedia.mediaType?.startsWith('video/') ? (
                <video 
                  data-media-id={heroMedia.id || 'hero-0'}
                  src={heroMedia.url} 
                  poster={getMediaMedium(heroMedia)} 
                  muted 
                  loop 
                  playsInline 
                />
              ) : (
                <img src={getMediaLarge(heroMedia)} alt={locationName} />
              )}
            </HeroMedia>
            <HeroExpandButton onClick={() => handleMediaClick(heroMedia, 0)}>
              ⛶
            </HeroExpandButton>
          </>
        )}
        
        <HeroContent>
          <LocationHeader>
            <LocationBadge variant={isReached ? (isFirst ? 'start' : isLast ? 'end' : 'reached') : 'locked'}>
              {isFirst ? '🚀' : isLast ? '🏆' : isReached ? '✓' : '🔒'}{' '}
              {isFirst 
                ? t('location.start', { defaultValue: 'Starting Point' })
                : isLast 
                  ? t('location.finish', { defaultValue: 'Finish Line' })
                  : isReached
                    ? t('location.reached', { defaultValue: 'Reached' })
                    : t('location.locked', { defaultValue: 'Locked' })}
            </LocationBadge>
            <LocationTitle>{locationName}</LocationTitle>
            <LocationMeta>
              <MetaItem>🗺️ {challengeName}</MetaItem>
              <MetaItem>
                ⭐ {location.pointsRequired.toLocaleString()} {t('location.points', { defaultValue: 'pts' })}
              </MetaItem>
              <MetaItem>
                📍 {locationIndex + 1}/{challenge.locations.length}
              </MetaItem>
            </LocationMeta>
          </LocationHeader>
        </HeroContent>
      </HeroSection>

      <ContentWrapper>
        <ContentRow>
        <MainContent xs={12} lg={8}>
          {isReached ? (
            <>
              {locationDescription && (
                <Section>
                  <SectionTitle>📖 {t('location.description', { defaultValue: 'About This Location' })}</SectionTitle>
                  <DescriptionContent>
                    <MarkdownRenderer text={locationDescription} />
                  </DescriptionContent>
                </Section>
              )}

              {location.medias && location.medias.length > 1 && (
                <Section>
                  <SectionTitle>📸 {t('location.gallery', { defaultValue: 'Gallery' })}</SectionTitle>
                  <MediaGallery>
                    {location.medias.slice(1).map((media, idx) => {
                      const isVideo = media.mediaType?.startsWith('video/');
                      const actualIndex = idx + 1; // +1 because we're skipping hero
                      return (
                        <MediaItem 
                          key={media.id || idx} 
                          isVideo={isVideo} 
                          isLarge={idx === 0}
                          onClick={() => handleMediaClick(media, actualIndex)}
                        >
                          {isVideo ? (
                            <>
                              <video 
                                data-media-id={media.id || `gallery-${actualIndex}`}
                                src={media.url} 
                                poster={getMediaMedium(media)} 
                                muted 
                                playsInline
                              />
                              <VideoPlayButton>▶</VideoPlayButton>
                            </>
                          ) : (
                            <img src={getMediaLarge(media)} alt={`${locationName} ${idx + 2}`} />
                          )}
                        </MediaItem>
                      );
                    })}
                  </MediaGallery>
                </Section>
              )}
            </>
          ) : (
            <LockedOverlay>
              <LockedIcon>🔒</LockedIcon>
              <LockedTitle>{t('location.lockedTitle', { defaultValue: 'Location Locked' })}</LockedTitle>
              <LockedDescription>
                {t('location.lockedDescription', {
                  defaultValue: 'Collect more points to unlock this location and see all its content!',
                })}
              </LockedDescription>
              <PointsBadge>
                ⭐ {location.totalPointsRequired.toLocaleString()}{' '}
                {t('location.totalPointsNeeded', { defaultValue: 'total points needed' })}
              </PointsBadge>
            </LockedOverlay>
          )}
        </MainContent>

        <SidebarContent xs={12} lg={4}>
          <Section>
            <SectionTitle>💬 {t('location.communityPosts', { defaultValue: 'Community Posts' })}</SectionTitle>

            {isReached && (
              <div style={{ marginBottom: '16px' }}>
                <Button color="primary" onClick={() => setCreatePostModalOpen(true)} block>
                  {t('location.createPost', { defaultValue: 'Share Your Experience' })}
                </Button>
              </div>
            )}

            {loadingPosts ? (
              <EmptyPosts>
                <span>⏳</span>
                {t('location.loadingPosts', { defaultValue: 'Loading posts...' })}
              </EmptyPosts>
            ) : posts.length > 0 ? (
              posts.map((post) => (
                <LocationPostCard key={post.id} post={post} />
              ))
            ) : (
              <EmptyPosts>
                <span>{isReached ? '💬' : '🔒'}</span>
                {isReached
                  ? t('location.noPosts', { defaultValue: 'No posts yet. Be the first to share!' })
                  : t('location.reachToPost', {
                      defaultValue: 'Reach this location to see and create posts!',
                    })}
              </EmptyPosts>
            )}
          </Section>
        </SidebarContent>
      </ContentRow>
      </ContentWrapper>

      {/* Fullscreen Media Viewer */}
      {fullscreenMedia && (
        <FullscreenOverlay onClick={closeFullscreen}>
          <FullscreenHeader onClick={(e) => e.stopPropagation()}>
            <FullscreenTitle>
              {locationName} • {t('location.gallery', { defaultValue: 'Gallery' })}
            </FullscreenTitle>
            <FullscreenClose onClick={closeFullscreen}>✕</FullscreenClose>
          </FullscreenHeader>
          
          <FullscreenContent onClick={(e) => e.stopPropagation()}>
            {fullscreenMedia[fullscreenIndex]?.type === 'video' ? (
              <video 
                key={fullscreenIndex}
                src={fullscreenMedia[fullscreenIndex].url} 
                poster={fullscreenMedia[fullscreenIndex].poster}
                controls 
                autoPlay
              />
            ) : (
              <img 
                key={fullscreenIndex}
                src={fullscreenMedia[fullscreenIndex]?.url} 
                alt={`${locationName} ${fullscreenIndex + 1}`} 
              />
            )}
          </FullscreenContent>
          
          <FullscreenNav onClick={(e) => e.stopPropagation()}>
            <FullscreenNavButton onClick={goToPrevMedia} disabled={fullscreenIndex === 0}>
              ←
            </FullscreenNavButton>
            <FullscreenCounter>
              {fullscreenIndex + 1} / {fullscreenMedia.length}
            </FullscreenCounter>
            <FullscreenNavButton onClick={goToNextMedia} disabled={fullscreenIndex === fullscreenMedia.length - 1}>
              →
            </FullscreenNavButton>
          </FullscreenNav>
          
          {fullscreenMedia.length > 1 && (
            <FullscreenThumbnails onClick={(e) => e.stopPropagation()}>
              {fullscreenMedia.map((item, idx) => (
                <FullscreenThumbnail 
                  key={idx} 
                  isActive={idx === fullscreenIndex}
                  onClick={() => setFullscreenIndex(idx)}
                >
                  <img src={item.poster || item.url} alt="" />
                </FullscreenThumbnail>
              ))}
            </FullscreenThumbnails>
          )}
        </FullscreenOverlay>
      )}

      {/* Create Post Modal */}
      {challengeId && (
        <CreatePostModal
          isOpen={createPostModalOpen}
          onClose={() => setCreatePostModalOpen(false)}
          challengeId={challengeId}
          locationId={locationId}
          onPostCreated={handlePostCreated}
        />
      )}
    </PageContainer>
  );
});

type LocationPostCardProps = {
  post: CommunityWallPost;
};

const LocationPostCard: React.FC<LocationPostCardProps> = observer(({ post }) => {
  const { t } = useTrans();
  const { reactionsByType, myReaction, toggleReaction } = usePostReactions(post);

  const handleReaction = useCallback((reactionType: string) => {
    toggleReaction(reactionType);
  }, [toggleReaction]);

  return (
    <PostCard>
      <PostHeader>
        <PostAvatar imageUrl={post.authorProfileImageUri}>
          {!post.authorProfileImage && (post.authorUsername?.charAt(0) || '?').toUpperCase()}
        </PostAvatar>
        <PostAuthor>
          <PostAuthorName>
            {post.authorUsername || t('location.anonymous', { defaultValue: 'Anonymous' })}
          </PostAuthorName>
          <PostDate>{dayjs(post.createdAt).fromNow()}</PostDate>
        </PostAuthor>
      </PostHeader>
      {post.text && <PostText>{post.text}</PostText>}
      {post.hasMedia && (
        <PostMediaGrid>
          {post.medias.slice(0, 4).map((media, idx) => {
            const isVideo = media.mediaType?.startsWith('video/');
            return (
              <PostMediaItem key={media.id || idx} isVideo={isVideo}>
                {isVideo ? (
                  <>
                    <video src={media.url} poster={getMediaSmallest(media)} />
                    <PostVideoOverlay><span>▶</span></PostVideoOverlay>
                  </>
                ) : (
                  <img src={getMediaSmallest(media)} alt="" />
                )}
              </PostMediaItem>
            );
          })}
        </PostMediaGrid>
      )}
      <PostReactions>
        <PostReactionButtons>
          {Object.entries(REACTION_TYPES).map(([key, emoji]) => {
            const count = reactionsByType[emoji] ?? 0;
            const isActive = myReaction === emoji;
            return (
              <PostReactionButton key={key} isActive={isActive} onClick={() => handleReaction(emoji)}>
                {emoji}
                {count > 0 && <PostReactionCount>{count}</PostReactionCount>}
              </PostReactionButton>
            );
          })}
        </PostReactionButtons>
      </PostReactions>
    </PostCard>
  );
});
